import {Result} from "../../data/models/bracket_api/Result";
import {Qualifier} from "../../data/models/bracket_api/Qualifier";
import {Bracket} from "../../data/models/bracket_api/Bracket";
import {FourWideResult} from "../../data/models/bracket_api/FourWideResult";
import {uuidv4} from "../UUIDGeneration";
import {UUID} from "crypto";

function generateStaticResults(bracket: Bracket, results: Result[]): Result[] {
    /**
     * This function generates static results for a given bracket that are the same regardless of number of competitors.
     *
     * @param {Bracket} bracket - The bracket for which to generate results.
     * @param {Result[]} results - An array of results.
     * @returns {Result[]} An array of generated results.
     */
    let returnResults: Result[] = [];
    returnResults.push({
        game_code: uuidv4() as UUID,
        bracket: bracket,
        round: 3,
        game: 1,
        competitor_1_game_code: results.find(result => result.round === 2 && result.game === 1)?.game_code,
        competitor_2_game_code: results.find(result => result.round === 2 && result.game === 2)?.game_code,
    })
    returnResults.push({
        game_code: uuidv4() as UUID,
        bracket: bracket,
        round: 3,
        game: 2,
        competitor_1_game_code: results.find(result => result.round === 2 && result.game === 3)?.game_code,
        competitor_2_game_code: results.find(result => result.round === 2 && result.game === 4)?.game_code,
    })
    returnResults.push({
        game_code: uuidv4() as UUID,
        bracket: bracket,
        round: 4,
        game: 1,
        competitor_1_game_code: returnResults[0].game_code,
        competitor_2_game_code: returnResults[1].game_code,
    })
    return returnResults;
}


function generateStaticResultsFourWide(bracket: Bracket, results: FourWideResult[]): FourWideResult[] {
    /**
     * This function generates static results for a given bracket that are the same regardless of number of competitors.
     *
     * @param {Bracket} bracket - The bracket for which to generate results.
     * @param {FourWideResult[]} results - An array of results.
     * @returns {FourWideResult[]} An array of generated results.
     */
    let returnResults: FourWideResult[] = [];
    returnResults.push({
        game_code: uuidv4() as UUID,
        bracket: bracket,
        round: 2,
        game: 1,
        competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
        competitor_2_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
        competitor_3_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
        competitor_4_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
    })
    returnResults.push({
        game_code: uuidv4() as UUID,
        bracket: bracket,
        round: 2,
        game: 2,
        competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
        competitor_2_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
        competitor_3_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
        competitor_4_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
    })
    returnResults.push({
        game_code: uuidv4() as UUID,
        bracket: bracket,
        round: 3,
        game: 1,
        competitor_1_game_code: returnResults[0].game_code,
        competitor_2_game_code: returnResults[0].game_code,
        competitor_3_game_code: returnResults[1].game_code,
        competitor_4_game_code: returnResults[1].game_code,
    })
    return returnResults;
}

export function generateResultsFromQualifiers(qualifiers: Qualifier[], bracket: Bracket): Result[] {
    let results: Result[] = [];
    console.log(`Results refreshed for ${bracket.race_class}`)
    if (bracket.number_of_competitors !== undefined) {
        if(bracket.number_of_competitors < 8){
            return [];
        }
        else if(bracket.number_of_competitors === 8){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 1,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                seed_1: 4,
                seed_2: 5,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                seed_1: 2,
                seed_2: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                seed_1: 3,
                seed_2: 6,
            })
        }
        else if(bracket.number_of_competitors === 9){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                winner: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                winner_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                seed_1: 5,
                seed_2: 6,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 2,
                seed_2: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 3,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                seed_1: 4,
                seed_2: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
            })
            // Round 3 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 2 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 3)?.game_code,
            })
            // Round 4 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 4,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 3 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 3 && result.game === 2)?.game_code,
            })
            return results;
        }
        else if(bracket.number_of_competitors === 10){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 1,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 3,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                seed_1: 4,
                seed_2: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 2,
                seed_2: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                seed_1: 5,
                seed_2: 6,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
            })
            // Round 3 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 2 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 3)?.game_code,
            })
            // Round 4 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 4,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 3 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 3 && result.game === 2)?.game_code,
            })
            return results;
        }
        else if (bracket.number_of_competitors === 11) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                winner: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                winner_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                seed_1: 6,
                seed_2: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 3,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 4,
                seed_2: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 2,
                seed_2: 11,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 5,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
            })
            // Round 3 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 2 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 3)?.game_code,
            })
            // Round 4 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 4,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 3 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 3 && result.game === 2)?.game_code,
            })
            return results;
        }
        else if (bracket.number_of_competitors === 12) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 1,
                seed_2: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                seed_1: 6,
                seed_2: 7,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 2,
                seed_2: 11,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 5,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 3,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 4,
                seed_2: 9,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 7)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 8)?.game_code,
            })
            // Round 3 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 1)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 3,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 2 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 2 && result.game === 4)?.game_code,
            })
            // Round 4 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 4,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 3 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 3 && result.game === 2)?.game_code,
            })
            return results;
        }
        else if (bracket.number_of_competitors === 13) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                winner: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                winner_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 7,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 4,
                seed_2: 11,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 5,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                seed_1: 2,
                seed_2: 13,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 3,
                seed_2: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 6,
                seed_2: 9,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
                seed_1: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 7)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 8)?.game_code,
            })
        }
        else if (bracket.number_of_competitors === 14) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 14)?.driver,
                seed_1: 1,
                seed_2: 14,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 4,
                seed_2: 11,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 5,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                seed_1: 2,
                seed_2: 13,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 7,
                seed_2: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 3,
                seed_2: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 6,
                seed_2: 9,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 7)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 8)?.game_code,
            })
        }
        else if (bracket.number_of_competitors === 15) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                winner: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                winner_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 8,
                seed_2: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                seed_1: 4,
                seed_2: 13,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 5,
                seed_2: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 15)?.driver,
                seed_1: 2,
                seed_2: 15,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 7,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 14)?.driver,
                seed_1: 3,
                seed_2: 14,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 6,
                seed_2: 11,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                seed_1: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 7)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 8)?.game_code,
            })
        }
        else if (bracket.number_of_competitors >= 16) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 16)?.driver,
                seed_1: 1,
                seed_2: 16,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 8,
                seed_2: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                seed_1: 4,
                seed_2: 13,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 5,
                seed_2: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 5,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 15)?.driver,
                seed_1: 2,
                seed_2: 15,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 6,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 7,
                seed_2: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 7,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 14)?.driver,
                seed_1: 3,
                seed_2: 14,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 8,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 6,
                seed_2: 11,
            })
            // Round 2 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 1,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 1)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 2)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 2,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 3)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 4)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 3,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 5)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 6)?.game_code,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 2,
                game: 4,
                competitor_1_game_code: results.find(result => result.round === 1 && result.game === 7)?.game_code,
                competitor_2_game_code: results.find(result => result.round === 1 && result.game === 8)?.game_code,
            })
        }
        results = results.concat(generateStaticResults(bracket, results));
        return results;
    }
    else{
        return [];
    }
}

export function generateResultsFromQualifiersFourWide(qualifiers: Qualifier[], bracket: Bracket): FourWideResult[] {
    let results: FourWideResult[] = [];
    if (bracket.number_of_competitors !== undefined) {
        if(bracket.number_of_competitors < 9){
            return [];
        }
        if (bracket.number_of_competitors === 9) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                first: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                first_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 4,
                seed_2: 9,
                seed_3: 5,
                seed_4: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                seed_1: 2,
                first: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                first_seed: 2,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                seed_1: 3,
                seed_3: 6,
                seed_4: 7,
            })
        }
        else if(bracket.number_of_competitors === 10){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                first: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                first_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 4,
                seed_3: 5,
                seed_4: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 2,
                seed_3: 7,
                seed_4: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 3,
                seed_3: 6,
                seed_4: 9,
            })
        }
        else if(bracket.number_of_competitors === 11){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                seed_1: 1,
                first: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                first_seed: 1,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                seed_1: 4,
                seed_2: 9,
                seed_3: 5,
                seed_4: 8,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 2,
                seed_3: 7,
                seed_4: 11,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 3,
                seed_3: 6,
                seed_4: 10,
            })
        }
        else if(bracket.number_of_competitors === 12){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 1,
                seed_3: 8,
                seed_4: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 4,
                seed_3: 5,
                seed_4: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 2,
                seed_3: 7,
                seed_4: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 3,
                seed_3: 6,
                seed_4: 11,
            })
        }
        else if(bracket.number_of_competitors === 13){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 1,
                seed_3: 8,
                seed_4: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 4,
                seed_2: 13,
                seed_3: 5,
                seed_4: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 2,
                seed_3: 7,
                seed_4: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 3,
                seed_3: 6,
                seed_4: 11,
            })
        }
        else if(bracket.number_of_competitors === 14){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 1,
                seed_3: 8,
                seed_4: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 4,
                seed_2: 13,
                seed_3: 5,
                seed_4: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 2,
                seed_3: 7,
                seed_4: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 14)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 3,
                seed_2: 14,
                seed_3: 6,
                seed_4: 11,
            })
        }
        else if(bracket.number_of_competitors === 15){
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 1,
                seed_3: 8,
                seed_4: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 4,
                seed_2: 13,
                seed_3: 5,
                seed_4: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 15)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 2,
                seed_2: 15,
                seed_3: 7,
                seed_4: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 14)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 3,
                seed_2: 14,
                seed_3: 6,
                seed_4: 11,
            })
        }
        else if (bracket.number_of_competitors >= 16) {
            // Round 1 results
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 1,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 1)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 16)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 8)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 9)?.driver,
                seed_1: 1,
                seed_2: 16,
                seed_3: 8,
                seed_4: 9,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 2,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 4)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 13)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 5)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 12)?.driver,
                seed_1: 4,
                seed_2: 13,
                seed_3: 5,
                seed_4: 12,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 3,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 2)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 15)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 7)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 10)?.driver,
                seed_1: 2,
                seed_2: 15,
                seed_3: 7,
                seed_4: 10,
            })
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 1,
                game: 4,
                competitor_1: qualifiers.find(qualifier => qualifier.seed === 3)?.driver,
                competitor_2: qualifiers.find(qualifier => qualifier.seed === 14)?.driver,
                competitor_3: qualifiers.find(qualifier => qualifier.seed === 6)?.driver,
                competitor_4: qualifiers.find(qualifier => qualifier.seed === 11)?.driver,
                seed_1: 3,
                seed_2: 14,
                seed_3: 6,
                seed_4: 11,
            })
        }
        results = results.concat(generateStaticResultsFourWide(bracket, results));
        return results;
    } else {
        return [];
    }
}

export function generateResultsFromQualifiersTopMoments64(qualifiers: Qualifier[], bracket: Bracket): Result[] {
    let results: Result[] = [];
    console.log(`Results refreshed for ${bracket.race_class}`)
    if (bracket.number_of_competitors !== undefined && bracket.number_of_competitors === 64) {
        // Helper function to get qualifier for a specific quadrant and seed within that quadrant
        // quadrantSeed: 1-16 (seed within the quadrant)
        // quadrant: 0=TopLeft, 1=TopRight, 2=BottomLeft, 3=BottomRight
        const getQualifierForQuadrant = (quadrantSeed: number, quadrant: number): Qualifier | undefined => {
            // Qualifier seed = (quadrantSeed - 1) * 4 + quadrant + 1
            // Example: Quadrant Seed 1, TopLeft (0) → Qualifier seed 1
            //          Quadrant Seed 1, TopRight (1) → Qualifier seed 2
            //          Quadrant Seed 2, TopLeft (0) → Qualifier seed 5
            const qualifierSeed = (quadrantSeed - 1) * 4 + quadrant + 1;
            return qualifiers.find(q => q.seed === qualifierSeed);
        };

        // Standard 16-entry bracket matchups (8 games per quadrant)
        const round1Matchups = [
            { seed1: 1, seed2: 16 },  // Game 1
            { seed1: 8, seed2: 9 },   // Game 2
            { seed1: 4, seed2: 13 },  // Game 3
            { seed1: 5, seed2: 12 },  // Game 4
            { seed1: 2, seed2: 15 },  // Game 5
            { seed1: 7, seed2: 10 },  // Game 6
            { seed1: 3, seed2: 14 },  // Game 7
            { seed1: 6, seed2: 11 },  // Game 8
        ];

        // Generate Round 1: 32 games (8 games per quadrant, 4 quadrants)
        // Quadrants: 0=TopLeft, 1=TopRight, 2=BottomLeft, 3=BottomRight
        for (let quadrant = 0; quadrant < 4; quadrant++) {
            round1Matchups.forEach((matchup, index) => {
                const gameNumber = quadrant * 8 + index + 1; // Game 1-32
                const qual1 = getQualifierForQuadrant(matchup.seed1, quadrant);
                const qual2 = getQualifierForQuadrant(matchup.seed2, quadrant);
                results.push({
                    game_code: uuidv4() as UUID,
                    bracket: bracket,
                    round: 1,
                    game: gameNumber,
                    competitor_1: qual1?.driver,
                    competitor_2: qual2?.driver,
                    seed_1: matchup.seed1,
                    seed_2: matchup.seed2,
                });
            });
        }

        // Generate Round 2: 16 games (4 games per quadrant)
        // Each game connects 2 Round 1 games within the same quadrant
        for (let quadrant = 0; quadrant < 4; quadrant++) {
            for (let gameInQuadrant = 1; gameInQuadrant <= 4; gameInQuadrant++) {
                const gameNumber = quadrant * 4 + gameInQuadrant; // Game 1-16
                const round1Game1 = quadrant * 8 + (gameInQuadrant - 1) * 2 + 1;
                const round1Game2 = round1Game1 + 1;
                const round1Result1 = results.find(r => r.round === 1 && r.game === round1Game1);
                const round1Result2 = results.find(r => r.round === 1 && r.game === round1Game2);
                results.push({
                    game_code: uuidv4() as UUID,
                    bracket: bracket,
                    round: 2,
                    game: gameNumber,
                    competitor_1_game_code: round1Result1?.game_code,
                    competitor_2_game_code: round1Result2?.game_code,
                });
            }
        }

        // Generate Round 3: 8 games (2 games per quadrant)
        for (let quadrant = 0; quadrant < 4; quadrant++) {
            for (let gameInQuadrant = 1; gameInQuadrant <= 2; gameInQuadrant++) {
                const gameNumber = quadrant * 2 + gameInQuadrant; // Game 1-8
                const round2Game1 = quadrant * 4 + (gameInQuadrant - 1) * 2 + 1;
                const round2Game2 = round2Game1 + 1;
                const round2Result1 = results.find(r => r.round === 2 && r.game === round2Game1);
                const round2Result2 = results.find(r => r.round === 2 && r.game === round2Game2);
                results.push({
                    game_code: uuidv4() as UUID,
                    bracket: bracket,
                    round: 3,
                    game: gameNumber,
                    competitor_1_game_code: round2Result1?.game_code,
                    competitor_2_game_code: round2Result2?.game_code,
                });
            }
        }

        // Generate Round 4: 4 games (1 game per quadrant - winner from each quadrant)
        for (let quadrant = 0; quadrant < 4; quadrant++) {
            const gameNumber = quadrant + 1; // Game 1-4
            const round3Game1 = quadrant * 2 + 1;
            const round3Game2 = round3Game1 + 1;
            const round3Result1 = results.find(r => r.round === 3 && r.game === round3Game1);
            const round3Result2 = results.find(r => r.round === 3 && r.game === round3Game2);
            results.push({
                game_code: uuidv4() as UUID,
                bracket: bracket,
                round: 4,
                game: gameNumber,
                competitor_1_game_code: round3Result1?.game_code,
                competitor_2_game_code: round3Result2?.game_code,
            });
        }

        // Generate Round 5: 2 games (Final Four - combines quadrant winners)
        // Game 1: Quadrant 0 (TopLeft) winner vs Quadrant 1 (TopRight) winner
        // Game 2: Quadrant 2 (BottomLeft) winner vs Quadrant 3 (BottomRight) winner
        const round4Result1 = results.find(r => r.round === 4 && r.game === 1); // TopLeft
        const round4Result2 = results.find(r => r.round === 4 && r.game === 2); // TopRight
        const round4Result3 = results.find(r => r.round === 4 && r.game === 3); // BottomLeft
        const round4Result4 = results.find(r => r.round === 4 && r.game === 4); // BottomRight

        results.push({
            game_code: uuidv4() as UUID,
            bracket: bracket,
            round: 5,
            game: 1,
            competitor_1_game_code: round4Result1?.game_code,
            competitor_2_game_code: round4Result2?.game_code,
        });

        results.push({
            game_code: uuidv4() as UUID,
            bracket: bracket,
            round: 5,
            game: 2,
            competitor_1_game_code: round4Result3?.game_code,
            competitor_2_game_code: round4Result4?.game_code,
        });

        // Generate Round 6: 1 game (Championship)
        const round5Result1 = results.find(r => r.round === 5 && r.game === 1);
        const round5Result2 = results.find(r => r.round === 5 && r.game === 2);

        results.push({
            game_code: uuidv4() as UUID,
            bracket: bracket,
            round: 6,
            game: 1,
            competitor_1_game_code: round5Result1?.game_code,
            competitor_2_game_code: round5Result2?.game_code,
        });

        return results;
    } else {
        return [];
    }
}